import * as React from "react";
import { Box, Button, Typography, styled, Grid } from "@mui/material";
import { StaticImage } from "gatsby-plugin-image";
import {
  SquareCardWithContent,
  SquareCardWithContentProps,
} from "../common/SquareCardWithContent";
import { FormattedMessage } from "../utils/FormattedMessage";
import { navigate, useIntl } from "gatsby-plugin-intl";
import { googleMapsUrl } from "../utils/constants";
import { MapPinIcon } from "../../icons/MapPin";
import { CarIcon } from "../../icons/Car";
import { EmailIcon } from "../../icons/Email";

const OverviewBox = styled(SquareCardWithContent)(({ theme }) => ({
  padding: `${theme.spacing(4)}`,
}));

type OverviewWrapperProps = Omit<
  SquareCardWithContentProps,
  "titleComponent" | "titleVariant"
> & {
  buttonText?: React.ReactNode;
  buttonTo?: string;
  wrapperProps?: React.ComponentProps<typeof Grid>;
};

function OverviewWrapper({
  buttonText,
  buttonTo,
  wrapperProps,
  ...props
}: OverviewWrapperProps) {
  return (
    <Grid
      p={2}
      sx={{ display: "flex", flexDirection: "column", position: "relative" }}
      {...wrapperProps}
      size={{
        xs: 12,
        md: 12,
        xl: 4,
      }}
    >
      <OverviewBox
        {...props}
        sx={{
          height: "100%",
          p: {
            xs: 2,
            sm: 1,
            md: 4,
          },
          pb: buttonText ? 4 : 2,
          ...props.sx,
        }}
        titleComponent="h3"
      />
      {buttonText && (
        <Button
          variant="contained"
          color="primary"
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            right: 0,
            transform: "translateX(-50%)",
          }}
          onClick={() => {
            if (buttonTo) navigate(buttonTo);
          }}
        >
          {buttonText}
        </Button>
      )}
    </Grid>
  );
}

function Overview() {
  const { formatMessage } = useIntl();
  return (
    <Box>
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <StaticImage
          src="../../images/contacts/contatti-aviosuperficie-scuola-volo-asti.jpg"
          alt="Contatti aviosuperficie scuola volo Asti: informazioni, servizi per piloti e appassionati in Piemonte"
          placeholder="blurred"
          layout="fullWidth"
          aspectRatio={8 / 3}
          transformOptions={{
            fit: "cover",
            cropFocus: "center",
          }}
          style={{
            height: "100%",
          }}
        />
      </Box>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <StaticImage
          src="../../images/contacts/contatti-aviosuperficie-scuola-volo-asti.jpg"
          alt="Contatti aviosuperficie scuola volo Asti: informazioni, servizi per piloti e appassionati in Piemonte"
          placeholder="blurred"
          layout="fullWidth"
          aspectRatio={16 / 9}
          transformOptions={{
            fit: "cover",
            cropFocus: "center",
          }}
          style={{
            height: "100%",
          }}
        />
      </Box>
      <Grid
        container
        spacing={2}
        sx={{
          mt: {
            xs: -10,
            md: -15,
            lg: -20,
          },
          position: "relative",
          px: {
            xs: 1,
            sm: 4,
          },
        }}
      >
        <OverviewWrapper
          Icon={CarIcon}
          title={<FormattedMessage id="common.contacts.address.title" />}
          onClick={() => {
            window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
          }}
        >
          <Typography variant="h5" component="p">
            <FormattedMessage id="common.contacts.address.street" />
            <br />
            <FormattedMessage id="common.contacts.address.city" />
          </Typography>
        </OverviewWrapper>
        <OverviewWrapper
          wrapperProps={{ size: { sm: 6, lg: 6 } }}
          Icon={MapPinIcon}
          title={<FormattedMessage id="common.airfield.coordinates.title" />}
          onClick={() => {
            window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
          }}
        >
          <Typography variant="h4" component="p">
            <FormattedMessage id="common.airfield.coordinates.latitude" />
            <br />
            <FormattedMessage id="common.airfield.coordinates.longitude" />
          </Typography>
        </OverviewWrapper>
        <OverviewWrapper
          wrapperProps={{ size: { sm: 6, lg: 6 } }}
          Icon={EmailIcon}
          title={<FormattedMessage id="common.contacts.email.title" />}
          onClick={() => {
            window.open(
              `mailto:${formatMessage({ id: "common.contacts.email.value" })}`,
              "_blank",
              "noopener,noreferrer"
            );
          }}
        >
          <Typography variant="h5" component="p">
            <FormattedMessage id="common.contacts.email.value" />
          </Typography>
        </OverviewWrapper>
      </Grid>
    </Box>
  );
}

export { Overview };
